import { useEffect, useState } from "react";
import axios from "axios";
import { useAccountant } from "@/contexts/AccountantContext";
import { getCurrencySymbol } from "@/lib/currencies";
import { todayInputDate } from "@/lib/formatDate";
import { useKipaToast } from "@/components/kipa";
import KipaIcon from "@/components/kipa/KipaIcon";
import { EXPENSE_CATEGORIES } from "@/lib/expenseCategoryIcons";
import { getQuickReplyDisplay } from "@/lib/quickReplyIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExpenseWizard({ open, onClose, onRecorded }) {
  const { fmt, currency } = useAccountant();
  const { showToast } = useKipaToast();
  const sym = getCurrencySymbol(currency);
  const [category, setCategory] = useState("fuel");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [accountType, setAccountType] = useState("");
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [expenseDate, setExpenseDate] = useState(() => todayInputDate());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setCategory("fuel");
      setDescription("");
      setAmount("");
      setAccountType("");
      setItemName("");
      setQuantity("");
      setExpenseDate(todayInputDate());
      setError("");
    }
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError("Enter a valid amount.");
      return;
    }
    if (!accountType) {
      setError("Select Cash or Bank — where did the money come from?");
      return;
    }
    if (category === "inventory_restock" && !itemName.trim()) {
      setError("Enter the product name for inventory restock.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post(`${API}/expenses/record`, {
        amount: amt,
        category,
        description: description.trim() || undefined,
        account_type: accountType,
        item: category === "inventory_restock" ? itemName.trim() : undefined,
        quantity:
          category === "inventory_restock" && quantity
            ? parseFloat(quantity)
            : undefined,
        expense_date: expenseDate || undefined,
      });
      const amtRecorded = res.data?.transaction?.amount ?? amt;
      showToast(`Expense recorded — ${fmt(amtRecorded)}`, "success");
      onRecorded?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not record expense.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/45"
      onClick={onClose}
      data-testid="expense-wizard"
    >
      <div
        className="w-full max-w-[430px] bg-white rounded-t-3xl p-5 pb-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUp 0.25s ease-out" }}
      >
        <div className="w-12 h-1.5 bg-[var(--br2)] rounded-full mx-auto mb-4" />
        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--rd)] mb-1">
          Record expense
        </div>
        <div className="text-[20px] font-black text-[var(--tx)] mb-1">Quick expense entry</div>
        <div className="text-[12px] text-[var(--tx2)] mb-4 leading-snug">
          Log money going out — no need to type full sentences.
        </div>

        <Field label="Expense date">
          <input
            type="date"
            className="s-inp"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            data-testid="expense-date"
          />
        </Field>

        <Field label="Category">
          <select
            className="s-inp"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="expense-category"
          >
            {EXPENSE_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>

        {category === "inventory_restock" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product name">
              <input
                className="s-inp"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder="e.g. paracetamol"
                data-testid="expense-item"
              />
            </Field>
            <Field label="Quantity">
              <input
                type="number"
                min="0"
                step="any"
                className="s-inp"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="expense-qty"
              />
            </Field>
          </div>
        )}

        <Field label="Description (optional)">
          <input
            className="s-inp"
            placeholder="e.g. Generator fuel for shop"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="expense-description"
          />
        </Field>

        <Field label={`Amount (${sym})`}>
          <input
            type="number"
            min="0"
            step="any"
            className="s-inp"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="expense-amount"
          />
        </Field>

        <Field label="Paid from">
          <div className="flex gap-2">
            {[
              { id: "cash" },
              { id: "bank" },
            ].map((opt) => {
              const { icon: ChipIcon, label } = getQuickReplyDisplay(opt);
              return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setAccountType(opt.id)}
                data-testid={`expense-account-${opt.id}`}
                className={`chip flex-1 inline-flex items-center justify-center gap-1.5 ${accountType === opt.id ? "chip-active" : ""}`}
              >
                {ChipIcon && (
                  <KipaIcon
                    icon={ChipIcon}
                    size="sm"
                    color={accountType === opt.id ? "#fff" : "var(--color-primary)"}
                  />
                )}
                {label}
              </button>
            );
            })}
          </div>
        </Field>

        {error && (
          <div className="text-[12px] text-[var(--rd)] font-semibold mb-2">{error}</div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={saving}
          data-testid="expense-submit"
          className="w-full bg-[var(--rd)] text-white rounded-2xl py-3.5 font-extrabold text-[14px] mb-2 disabled:opacity-60"
        >
          {saving ? "Recording…" : "Record expense"}
        </button>
        <button type="button" onClick={onClose} className="w-full text-[var(--tx3)] py-2 text-[12px]">
          Cancel
        </button>

        <style>{`
          .s-inp {
            background: var(--sf2);
            border: 1.5px solid var(--br);
            border-radius: 12px;
            padding: 10px 14px;
            color: var(--tx);
            font-size: 15px;
            width: 100%;
            font-family: inherit;
            outline: none;
          }
          .s-inp:focus { border-color: var(--ac); }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">{label}</div>
      {children}
    </div>
  );
}
